import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthBootstrap } from "../../context/AuthBootstrapContext";
import "./index.css";
import reaction1 from "../../assets/reactions/Beautiful.png";
import reaction2 from "../../assets/reactions/Awestruck.png";
import reaction3 from "../../assets/reactions/Mindbending.png";
import reaction4 from "../../assets/reactions/Thought Provoking.png";
import reaction5 from "../../assets/reactions/Reflective.png";

const API_BASE = "https://api-2026.fikacreatives.co";
const POSTS_API = `${API_BASE}/api/posts`;
const REACTIONS_API = `${API_BASE}/api/post-reactions`;


const REACTIONS = [
  { id: "r1", label: "Beautiful",        src: reaction1 },
  { id: "r2", label: "Awestruck",        src: reaction2 },
  { id: "r3", label: "Mindbending",      src: reaction3 },
  { id: "r4", label: "Thought Provoking", src: reaction4 },
  { id: "r5", label: "Reflective",       src: reaction5 },
];

function getEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname === "youtu.be") {
      const id = u.pathname.slice(1).split("?")[0];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.slice(1).split("?")[0];
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
    return null;
  } catch {
    return null;
  }
}

function PostAvatar({ src, name, size = 36 }) {
  const initials = name
    ? name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";
  return src ? (
    <img src={src} alt={name} className="studio__overlayAvatar" style={{ width: size, height: size }} />
  ) : (
    <div className="studio__overlayAvatarFallback" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </div>
  );
}

function ReactionNoteModal({ reaction, post, onClose }) {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const authorFirstName = post.first_name || "them";
  const authorName = `${post.first_name || ""} ${post.last_name || ""}`.trim() || "this maker";

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSend() {
    if (!note.trim()) return;
    setSending(true);
    setError("");
    try {
      const res = await fetch(REACTIONS_API, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          post_id: post.id,
          reaction_id: reaction.id,
          reaction_label: reaction.label,
          note: note.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.error || "Could not send note");
      setSent(true);
      setTimeout(onClose, 1600);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="studio__backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="studio__modal">
        <div className="studio__modalHeader">
          <h2 className="studio__modalTitle">Write a note to {authorFirstName}</h2>
          <button className="studio__modalClose" onClick={onClose} type="button">×</button>
        </div>

        {reaction.src && (
          <div className="studio__noteReactionPreview">
            <img src={reaction.src} alt={reaction.label} />
          </div>
        )}

        <p className="studio__noteAuthor">To: {authorName}</p>

        <textarea
          className="studio__captionInput"
          placeholder={`Add a note to ${authorFirstName}`}
          value={note}
          onChange={(e) => { setNote(e.target.value); setError(""); }}
          rows={4}
          autoFocus
        />

        {error && <p className="studio__error">{error}</p>}

        {sent ? (
          <p className="studio__noteSent">Note sent!</p>
        ) : (
          <button
            className="studio__postBtn"
            onClick={handleSend}
            disabled={sending}
            type="button"
          >
            {sending ? "Sending…" : "Send reaction"}
          </button>
        )}
      </div>
    </div>
  );
}

function PostCard({ post, viewerAvatarUrl }) {
  const [reactPanelOpen, setReactPanelOpen] = useState(false);
  const [noteModal, setNoteModal] = useState(null);

  const embedUrl = getEmbedUrl(post.video_url);
  const name = `${post.first_name || ""} ${post.last_name || ""}`.trim() || "Fika Member";
  const specialty = post.creative_specialty || null;
  const neighborhood = post.neighborhood || null;
  const subtitle = specialty && neighborhood
    ? `${specialty} based in ${neighborhood}`
    : specialty || neighborhood || null;

  function openReaction(reaction) {
    setReactPanelOpen(false);
    setNoteModal(reaction);
  }

  return (
    <>
      <div className="studio__card">
        <div className="studio__cardInner">
          {post.image_url && (
            <img src={post.image_url} alt="Project" className="studio__cardImage" />
          )}
          {!post.image_url && embedUrl && (
            <div className="studio__cardVideoWrap">
              <iframe
                src={embedUrl}
                title="Project video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="studio__cardIframe"
                style={{ border: "none" }}
              />
            </div>
          )}
          {!post.image_url && !embedUrl && (
            <div className="studio__cardEmpty" />
          )}

          {reactPanelOpen && (
            <div className="studio__reactPanel">
              <div className="studio__reactPanelItems">
                {REACTIONS.map((r) => (
                  <button
                    key={r.id}
                    className="studio__reactionItem"
                    onClick={() => openReaction(r)}
                    title={r.label}
                    type="button"
                  >
                    {r.src
                      ? <img src={r.src} alt={r.label} className="studio__reactionImg" />
                      : <div className="studio__reactionPlaceholder" />
                    }
                  </button>
                ))}
              </div>
              <button
                className="studio__reactPanelClose"
                onClick={() => setReactPanelOpen(false)}
                type="button"
              >
                ×
              </button>
            </div>
          )}

          <div className="studio__cardOverlay">
            <div className="studio__overlayLeft">
              <PostAvatar src={post.profile_image_url} name={name} size={36} />
              <div className="studio__overlayInfo">
                <span className="studio__overlayName">{name}</span>
                {subtitle && <span className="studio__overlaySub">{subtitle}</span>}
              </div>
            </div>
            <button
              className="studio__reactBtn"
              type="button"
              onClick={() => setReactPanelOpen((v) => !v)}
            >
              {viewerAvatarUrl && (
                <img src={viewerAvatarUrl} alt="" className="studio__reactAvatar" />
              )}
              React
            </button>
          </div>
        </div>
      </div>

      {noteModal && (
        <ReactionNoteModal
          reaction={noteModal}
          post={post}
          onClose={() => setNoteModal(null)}
        />
      )}
    </>
  );
}

function PostModal({ onClose, onPosted }) {
  const [mediaType, setMediaType] = useState("image");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    return () => { if (imagePreview) URL.revokeObjectURL(imagePreview); };
  }, [imagePreview]);

  function onFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError("");
  }

  async function handlePost() {
    if (mediaType === "image" && !imageFile) { setError("Please select an image."); return; }
    if (mediaType === "video" && !videoUrl.trim()) { setError("Please enter a video URL."); return; }

    setPosting(true);
    setError("");
    try {
      let image_url = null;

      if (mediaType === "image" && imageFile) {
        const prepRes = await fetch(POSTS_API, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "create_upload_url",
            file_name: imageFile.name,
            content_type: imageFile.type || "application/octet-stream",
          }),
        });
        const prep = await prepRes.json();
        if (!prepRes.ok || !prep?.ok) throw new Error(prep?.error || "Could not prepare upload");
        await fetch(prep.signedUrl, {
          method: "PUT",
          headers: { "Content-Type": imageFile.type || "application/octet-stream" },
          body: imageFile,
        });
        image_url = prep.publicUrl;
      }

      const createRes = await fetch(POSTS_API, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create",
          image_url,
          video_url: mediaType === "video" ? videoUrl.trim() : null,
          caption: caption.trim() || null,
        }),
      });
      const created = await createRes.json();
      if (!createRes.ok || !created?.ok) throw new Error(created?.error || "Could not post");

      onPosted(created.post);
      onClose();
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="studio__backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="studio__modal">
        <div className="studio__modalHeader">
          <h2 className="studio__modalTitle">Share what you're working on</h2>
          <button className="studio__modalClose" onClick={onClose} type="button">×</button>
        </div>

        <div className="studio__mediaToggle">
          <button
            className={`studio__mediaTab${mediaType === "image" ? " studio__mediaTab--active" : ""}`}
            onClick={() => setMediaType("image")}
            type="button"
          >
            Image
          </button>
          <button
            className={`studio__mediaTab${mediaType === "video" ? " studio__mediaTab--active" : ""}`}
            onClick={() => setMediaType("video")}
            type="button"
          >
            Video link
          </button>
        </div>

        {mediaType === "image" && (
          <button className="studio__uploadArea" type="button" onClick={() => fileRef.current?.click()}>
            {imagePreview ? (
              <img src={imagePreview} alt="Preview" className="studio__uploadPreview" />
            ) : (
              <div className="studio__uploadPlaceholder">
                <span className="studio__uploadIcon">+</span>
                <span>Tap to add image</span>
              </div>
            )}
          </button>
        )}

        {mediaType === "video" && (
          <input
            type="url"
            className="studio__urlInput"
            placeholder="Paste a YouTube or Vimeo link"
            value={videoUrl}
            onChange={(e) => { setVideoUrl(e.target.value); setError(""); }}
          />
        )}

        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFileChange} />

        <textarea
          className="studio__captionInput"
          placeholder="What's the story behind this?"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={3}
        />

        {error && <p className="studio__error">{error}</p>}

        <button className="studio__postBtn" onClick={handlePost} disabled={posting} type="button">
          {posting ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}

export default function StudioPage() {
  const { profileAssets } = useAuthBootstrap();
  const viewerAvatarUrl = profileAssets?.profile_image_url || null;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const carouselRef = useRef(null);

  useEffect(() => {
    fetch(POSTS_API, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => { if (data?.ok) setPosts(data.posts || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const slideWidth = el.firstChild?.offsetWidth || 1;
    const gap = 16;
    const index = Math.round(el.scrollLeft / (slideWidth + gap));
    setActiveIndex(Math.max(0, Math.min(index, posts.length - 1)));
  }, [posts.length]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  function handlePosted(newPost) {
    setPosts((prev) => [newPost, ...prev]);
    setActiveIndex(0);
    requestAnimationFrame(() => {
      if (carouselRef.current) carouselRef.current.scrollLeft = 0;
    });
  }

  const activeCaption = posts[activeIndex]?.caption || null;

  return (
    <div className="studio__page">
      <div className="studio__header">
        <h1 className="studio__heading">
          Post a current/previous project you're proud of!{" "}
          <span style={{ display: "inline-block", background: "#C8A882", color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 999, verticalAlign: "middle", fontFamily: "Suisse" }}>New</span>
        </h1>
        <button className="studio__newBtn" onClick={() => setModalOpen(true)} type="button">
          + Add creative work
        </button>
      </div>

      {loading && <p className="studio__empty">Loading…</p>}

      {!loading && posts.length === 0 && (
        <p className="studio__empty">No posts yet. Be the first to share something.</p>
      )}

      {posts.length > 0 && (
        <>
          <div className="studio__carousel" ref={carouselRef}>
            {posts.map((post) => (
              <div key={post.id} className="studio__slide">
                <PostCard post={post} viewerAvatarUrl={viewerAvatarUrl} />
              </div>
            ))}
          </div>

          {posts.length > 1 && (
            <div className="studio__dots">
              {posts.map((_, i) => (
                <div key={i} className={`studio__dot${i === activeIndex ? " studio__dot--active" : ""}`} />
              ))}
            </div>
          )}

          {activeCaption && (
            <p className="studio__caption">{activeCaption}</p>
          )}
        </>
      )}

      {modalOpen && (
        <PostModal onClose={() => setModalOpen(false)} onPosted={handlePosted} />
      )}
    </div>
  );
}
